import { useLocalStorageState } from 'ahooks';
import { isEqual } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';

import { useDraftParams } from '@/hooks/use-draft-params';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import { useNoteUserSourceActions } from '@/hooks/use-note-user-source-actions';
import { useDocumentId } from '@/hooks/use-document-id';
import { useResolvedUserSourceIds, useNote, useNoteLazy, useNoteWriter } from '@/hooks/rxdb-models';
import type { NoteUserSourceMap } from '@/hooks/use-source-list-model';

import { Tab } from '@flintmint/web-shared/components/tab';
import { Tabs } from '@flintmint/web-shared/components/tabs';

import { AIContextSection } from '@/components/ai-context-section';
import { CortexPreviewModal } from '@/components/cortex-preview-modal';
import { ContextItemsModal, type UploadFlowStep } from '@/components/context-items-modal';

import type { RxDocument } from 'rxdb';
import type { Note } from '@flintmint/core-shared/rxdb';
import type { OpenSection } from '@/components/ai-context';
import type { NoteVariant } from '@/providers/rxdb/constants';

import { appRoutes } from '@/constants/routes';
import { getDocumentIdFromPathname } from '@/utils/helper-utils';

import styles from './ai-context-sources-section.module.scss';
import { TabPanel } from '@flintmint/web-shared/components/tab-panel';
import { TabPanels } from '@flintmint/web-shared/components/tab-panels';
import { SourcePanel } from '@/components/ai-sources-section/source-panel';

export const AiSourcesSection = memo((props: AiSourcesSectionProps) => {
  const { onOpenChange } = props;

  const { id, isDraft } = useDocumentId();
  const { data: note } = useNote(!isDraft ? id : undefined);
  const noteUserSources = useStableNoteUserSources(note?.userSources);
  const rawAttachedUserSourceIds = useMemo(() => Object.keys(noteUserSources), [noteUserSources]);

  const attachedUserSourceIds = useResolvedUserSourceIds(rawAttachedUserSourceIds);

  const [activeTab, setActiveTab] = useState('on-this-note');
  const [cortexPreviewId, setCortexPreviewId] = useState<string>();
  const [isContextItemsModalOpen, setIsContextItemsModalOpen] = useState(false);
  const [contextModalStep, setContextModalStep] = useState<UploadFlowStep>('add-items');
  const [isLastItemVisible, setIsLastItemVisible] = useState(false);

  // context-items
  const [openSection] = useLocalStorageState<OpenSection>('ai-context', {
    listenStorageChange: true,
  });

  const { draftType: newNoteVariant } = useDraftParams();
  const noteVariant = (note?.variant || newNoteVariant) as NoteVariant;

  const { mutate: upsertNote } = useNoteWriter(note, id, { variant: noteVariant, createIfMissing: isDraft });
  const { query: queryNote } = useNoteLazy();

  const navigateWithSearch = useNavigateWithSearch();

  const { onAttachUserSources, onDetachUserSources, onReplaceUserSource, onUserSourceEnabledChange } = useNoteUserSourceActions({
    noteId: id,
    note,
    upsertNote,
    queryNote,
    onCurrentNoteSaved: (savedNoteId, targetNoteId) => {
      const currentNoteId = getDocumentIdFromPathname(window.location.pathname, [appRoutes.library]);
      if (currentNoteId === targetNoteId) {
        navigateWithSearch(`${appRoutes.library}/${savedNoteId}`, {
          replace: true,
          removeSearchParams: ['draftType', 'domain'],
        });
      }
    },
  });

  const openManageContextItems = () => {
    setContextModalStep('manage-items');
    setIsContextItemsModalOpen(true);
  };

  const openAddContextItems = () => {
    setContextModalStep('add-items');
    setIsContextItemsModalOpen(true);
  };

  const toggleAttachment = useCallback(
    (sourceId: string, attached: boolean) => {
      attached ? onDetachUserSources?.([sourceId]) : onAttachUserSources?.([sourceId]);
    },
    [onAttachUserSources, onDetachUserSources]
  );

  return (
    <>
      <AIContextSection
        icon="ai-brain-01-bulk"
        open={openSection === 'context-items'}
        title="Sources"
        dataCount={{
          total: attachedUserSourceIds.length,
          enabled: attachedUserSourceIds.filter((id) => noteUserSources[id]?.enabled).length,
        }}
        collapseTestId="context-items-section-collapse"
        manageTestId="ai-context-manage-context-items"
        onManage={openManageContextItems}
        onOpenChange={onOpenChange}
        lastItemVisible={isLastItemVisible}
        header={
          <Tabs
            value={activeTab}
            appearance="elevated"
            size="small"
            onChange={(activeTab) => {
              // Prevent transient footer blur glitches by clearing stale visibility from the previous tab.
              setIsLastItemVisible(false);
              setActiveTab(activeTab);
            }}
          >
            <Tab id="on-this-note" name="Attached to this Note" />
            <Tab id="from-source-library" name="From Source Library" />
          </Tabs>
        }
        container={
          <>
            <TabPanels value={activeTab} className={styles.panels} data-testid="context-sources-tab-panels">
              <TabPanel id="on-this-note">
                <SourcePanel
                  activeTab={activeTab}
                  setIsLastItemVisible={setIsLastItemVisible}
                  showAllUserSources={false}
                  attachedUserSourceIds={attachedUserSourceIds}
                  openAddContextItems={openAddContextItems}
                  openManageContextItems={openManageContextItems}
                  onUserSourceEnabledChange={onUserSourceEnabledChange}
                  toggleAttachment={toggleAttachment}
                  setCortexPreviewId={setCortexPreviewId}
                  onDetachUserSources={onDetachUserSources}
                  noteUserSources={noteUserSources}
                  showNoAttachedState={!attachedUserSourceIds.length}
                />
              </TabPanel>
              <TabPanel id="from-source-library">
                <SourcePanel
                  activeTab={activeTab}
                  setIsLastItemVisible={setIsLastItemVisible}
                  showAllUserSources
                  attachedUserSourceIds={attachedUserSourceIds}
                  openAddContextItems={openAddContextItems}
                  onUserSourceEnabledChange={onUserSourceEnabledChange}
                  toggleAttachment={toggleAttachment}
                  setCortexPreviewId={setCortexPreviewId}
                  onDetachUserSources={onDetachUserSources}
                  noteUserSources={noteUserSources}
                />
              </TabPanel>
            </TabPanels>
          </>
        }
      />
      {!!cortexPreviewId && (
        <CortexPreviewModal
          open
          noteVariant={noteVariant}
          userSourceAttached={attachedUserSourceIds.includes(cortexPreviewId)}
          userSourceId={cortexPreviewId}
          onReplaceWithNew={onReplaceUserSource}
          toggleAttachment={toggleAttachment}
          onClose={() => setCortexPreviewId(undefined)}
        />
      )}
      <ContextItemsModal
        open={isContextItemsModalOpen}
        onClose={() => setIsContextItemsModalOpen(false)}
        initialStep={contextModalStep}
        noteVariant={noteVariant}
        onAttachToNote={onAttachUserSources}
        onDetachFromNote={onDetachUserSources}
        onUserSourceEnabledChange={onUserSourceEnabledChange}
        onReplaceWithNew={onReplaceUserSource}
      />
    </>
  );
});

interface AiSourcesSectionProps {
  note?: RxDocument<Note>;
  onOpenChange?: (open: boolean) => void;
}

const EMPTY_USER_SOURCES: NoteUserSourceMap = {};

// The note doc is replaced on every autosave, so `userSources` gets a new identity even when its
// contents are unchanged; keep the last deep-equal value so the source lists don't re-sort/re-render.
const useStableNoteUserSources = (userSources?: NoteUserSourceMap) => {
  const next = userSources ?? EMPTY_USER_SOURCES;
  const [stable, setStable] = useState(next);

  if (stable !== next && !isEqual(stable, next)) {
    setStable(next);
    return next;
  }

  return stable;
};
